import { z } from 'zod'

/**
 * Données du module Google synchronisées par `marketing-sync` (Windsor.ai), lues par `marketing_status_v1`,
 * `marketing_overview_v1` et `marketing_breakdown_v1`. Règles de calcul et d'alerte : `docs/24-Module-Google.md`.
 * Aucun chiffre n'est inventé : sans donnée, un ratio vaut `null` et s'affiche « — ».
 */

const num = z.coerce.number()
const nullableNum = z.union([z.coerce.number(), z.null()])

export const marketingSourceSchema = z.enum(['GOOGLE_ADS', 'GA4', 'SEARCH_CONSOLE'])
export type MarketingSource = z.infer<typeof marketingSourceSchema>

export const marketingSourceLabels: Record<MarketingSource, string> = {
  GOOGLE_ADS: 'Google Ads',
  GA4: 'Google Analytics 4',
  SEARCH_CONSOLE: 'Search Console',
}

export const marketingConnectionSchema = z.object({
  source: marketingSourceSchema,
  provider: z.string(),
  accountId: z.string(),
  accountLabel: z.string().nullable(),
  status: z.enum(['NOT_SYNCED', 'CONNECTED', 'ERROR']),
  lastAttemptAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorMessage: z.string().nullable(),
  dataUntil: z.string().nullable(),
  running: z.boolean(),
})
export type MarketingConnection = z.infer<typeof marketingConnectionSchema>

export const marketingRunSchema = z.object({
  id: z.string(),
  source: marketingSourceSchema,
  trigger: z.enum(['MANUAL', 'SCHEDULED', 'BACKFILL']),
  from: z.string(),
  to: z.string(),
  status: z.enum(['RUNNING', 'SUCCEEDED', 'FAILED']),
  rows: z.number().nullable(),
  errorCode: z.string().nullable(),
  startedAt: z.string(),
  durationMs: z.number().nullable(),
})
export type MarketingRun = z.infer<typeof marketingRunSchema>

export const marketingStatusSchema = z.object({
  connections: z.array(marketingConnectionSchema),
  runs: z.array(marketingRunSchema),
})
export type MarketingStatus = z.infer<typeof marketingStatusSchema>

const adsTotalsSchema = z.object({
  impressions: num,
  clicks: num,
  cost: num,
  conversions: num,
  conversionValue: num,
  days: num,
})
const ga4TotalsSchema = z.object({ sessions: num, pageViews: num, keyEvents: num, days: num })
const gscTotalsSchema = z.object({ clicks: num, impressions: num, position: nullableNum, days: num })
const funnelStepsSchema = z.object({
  leads: num,
  qualified: num,
  proposalsSent: num,
  signed: num,
  signedValue: num,
})
export type FunnelSteps = z.infer<typeof funnelStepsSchema>

export const leadChannelSchema = z.enum([
  'GOOGLE_ADS',
  'GOOGLE_ORGANIC',
  'GOOGLE_BUSINESS',
  'SOCIAL',
  'EMAIL',
  'REFERRAL',
  'OTHER_CAMPAIGN',
  'DIRECT',
  'UNKNOWN',
])
export type LeadChannel = z.infer<typeof leadChannelSchema>

export const leadChannelLabels: Record<LeadChannel, string> = {
  GOOGLE_ADS: 'Google Ads',
  GOOGLE_ORGANIC: 'Google naturel',
  GOOGLE_BUSINESS: 'Fiche Google',
  SOCIAL: 'Réseaux sociaux',
  EMAIL: 'Courriel',
  REFERRAL: 'Site référent',
  OTHER_CAMPAIGN: 'Autre campagne',
  DIRECT: 'Direct',
  UNKNOWN: 'Inconnu',
}

export const marketingOverviewSchema = z.object({
  from: z.string(),
  to: z.string(),
  previousFrom: z.string(),
  previousTo: z.string(),
  ads: z.object({
    current: adsTotalsSchema,
    previous: adsTotalsSchema,
    daily: z.array(
      z.object({ date: z.string(), impressions: num, clicks: num, cost: num, conversions: num }),
    ),
  }),
  ga4: z.object({
    current: ga4TotalsSchema,
    previous: ga4TotalsSchema,
    daily: z.array(
      z.object({ date: z.string(), sessions: num, activeUsers: num, pageViews: num, keyEvents: num }),
    ),
  }),
  gsc: z.object({ current: gscTotalsSchema, previous: gscTotalsSchema }),
  funnel: z.object({
    byChannel: z.array(funnelStepsSchema.extend({ channel: leadChannelSchema })),
    byCampaign: z.array(funnelStepsSchema.extend({ campaign: z.string() })),
    previousByChannel: z.array(funnelStepsSchema.extend({ channel: leadChannelSchema })),
    daily: z.array(z.object({ date: z.string(), leads: num, googleAdsLeads: num })),
  }),
})
export type MarketingOverview = z.infer<typeof marketingOverviewSchema>

export const adsBreakdownRowSchema = z.object({
  campaign: z.string(),
  adGroup: z.string(),
  dimension: z.string(),
  matchType: z.string(),
  campaignStatus: z.string().nullable(),
  campaignType: z.string().nullable(),
  qualityScore: nullableNum,
  impressions: num,
  clicks: num,
  cost: num,
  conversions: num,
  conversionValue: num,
  previousCost: nullableNum,
  previousClicks: nullableNum,
  previousConversions: nullableNum,
})
export type AdsBreakdownRow = z.infer<typeof adsBreakdownRowSchema>

export const ga4BreakdownRowSchema = z.object({
  dimension: z.string(),
  sessions: num,
  pageViews: num,
  keyEvents: num,
  eventCount: num,
  days: num,
  previousSessions: nullableNum,
  previousKeyEvents: nullableNum,
})
export type Ga4BreakdownRow = z.infer<typeof ga4BreakdownRowSchema>

export const gscBreakdownRowSchema = z.object({
  dimension: z.string(),
  clicks: num,
  impressions: num,
  position: nullableNum,
  previousClicks: nullableNum,
  previousImpressions: nullableNum,
  previousPosition: nullableNum,
})
export type GscBreakdownRow = z.infer<typeof gscBreakdownRowSchema>

export type AdsKind = 'CAMPAIGN' | 'AD_GROUP' | 'KEYWORD' | 'SEARCH_TERM' | 'DEVICE' | 'GEO'
export type Ga4Kind = 'CHANNEL' | 'SOURCE_MEDIUM' | 'LANDING_PAGE' | 'DEVICE' | 'CITY' | 'EVENT'
export type GscKind = 'QUERY' | 'PAGE' | 'DEVICE' | 'COUNTRY'

export interface SyncResult {
  source: MarketingSource
  status: 'SUCCEEDED' | 'FAILED' | 'SKIPPED'
  rows?: number
  error?: string
}

export interface ConnectionTestResult {
  source: MarketingSource
  ok: boolean
  accountFound: boolean
  errorCode: string | null
}

// ——— Calculs ———

/** Division sûre : sans dénominateur, aucun ratio (jamais 0 par défaut). */
export function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null
}

/** Variation relative vs période précédente; `null` sans base de comparaison. */
export function change(current: number, previous: number): number | null {
  return previous > 0 ? (current - previous) / previous : null
}

/**
 * La période précédente a-t-elle moins de la moitié des jours de données de la période courante ? (Campagne
 * lancée le 23 sept. 2026 : comparer 30 jours à 3 jours afficherait « +1 800 % ».) La variation est alors masquée.
 */
export function previousIncomplete(current: { days: number }, previous: { days: number }): boolean {
  return current.days > 0 && previous.days < current.days * 0.5
}

export function sumSteps(rows: readonly FunnelSteps[]): FunnelSteps {
  return rows.reduce<FunnelSteps>(
    (total, row) => ({
      leads: total.leads + row.leads,
      qualified: total.qualified + row.qualified,
      proposalsSent: total.proposalsSent + row.proposalsSent,
      signed: total.signed + row.signed,
      signedValue: total.signedValue + row.signedValue,
    }),
    { leads: 0, qualified: 0, proposalsSent: 0, signed: 0, signedValue: 0 },
  )
}

export function channelSteps(
  rows: readonly (FunnelSteps & { channel: LeadChannel })[],
  channel: LeadChannel,
): FunnelSteps {
  return sumSteps(rows.filter((row) => row.channel === channel))
}

export interface MarketingKpis {
  cost: number
  impressions: number
  clicks: number
  ctr: number | null
  cpc: number | null
  adsConversions: number
  costPerAdsConversion: number | null
  leads: number
  adsLeads: number
  qualified: number
  proposalsSent: number
  signed: number
  adsSigned: number
  signedValue: number
  adsSignedValue: number
  costPerLead: number | null
  costPerContract: number | null
  roas: number | null
  leadToContract: number | null
  sessions: number
}

/**
 * Indicateurs de la vue d'ensemble. Coût par demande, par contrat et ROAS : dépenses Google Ads rapportées aux seules
 * demandes attribuées à Google Ads (jamais à toutes les demandes, ce qui flatterait le coût).
 */
export function marketingKpis(overview: MarketingOverview): MarketingKpis {
  const ads = overview.ads.current
  const all = sumSteps(overview.funnel.byChannel)
  const adsFunnel = channelSteps(overview.funnel.byChannel, 'GOOGLE_ADS')
  return {
    cost: ads.cost,
    impressions: ads.impressions,
    clicks: ads.clicks,
    ctr: ratio(ads.clicks, ads.impressions),
    cpc: ratio(ads.cost, ads.clicks),
    adsConversions: ads.conversions,
    costPerAdsConversion: ratio(ads.cost, ads.conversions),
    leads: all.leads,
    adsLeads: adsFunnel.leads,
    qualified: all.qualified,
    proposalsSent: all.proposalsSent,
    signed: all.signed,
    adsSigned: adsFunnel.signed,
    signedValue: all.signedValue,
    adsSignedValue: adsFunnel.signedValue,
    costPerLead: ratio(ads.cost, adsFunnel.leads),
    costPerContract: ratio(ads.cost, adsFunnel.signed),
    // Sans demande attribuée à Google Ads, la valeur attribuée est inconnue, pas nulle : aucun ROAS de 0.
    roas: adsFunnel.leads > 0 ? ratio(adsFunnel.signedValue, ads.cost) : null,
    leadToContract: ratio(all.signed, all.leads),
    sessions: overview.ga4.current.sessions,
  }
}

// ——— Santé du tracking ———

export type HealthSeverity = 'alert' | 'warning' | 'info'

export interface HealthIssue {
  code: string
  severity: HealthSeverity
  title: string
  detail: string
}

const STALE_AFTER_MS = 24 * 60 * 60 * 1000
const dayLabel = new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const formatDay = (isoDate: string) => dayLabel.format(new Date(`${isoDate}T12:00:00Z`))

function median(values: readonly number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  if (sorted.length === 0) return 0
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? (sorted[middle] ?? 0) : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
}

/**
 * Points à vérifier, du plus grave au moins grave. Chaque règle est un constat sur les données, jamais une cause
 * présumée : le texte dit ce qui est observé et où regarder.
 */
export function trackingHealth(
  overview: MarketingOverview | null,
  connections: readonly MarketingConnection[],
  now: Date,
): HealthIssue[] {
  const issues: HealthIssue[] = []

  for (const connection of connections) {
    const label = marketingSourceLabels[connection.source]
    if (connection.status === 'ERROR') {
      issues.push({
        code: `SYNC_FAILED_${connection.source}`,
        severity: 'alert',
        title: `Synchronisation ${label} en échec`,
        detail: `Dernière erreur : ${connection.lastErrorCode ?? 'inconnue'}. Les données affichées datent de la dernière réussite.`,
      })
    } else if (
      connection.lastSuccessAt &&
      now.getTime() - new Date(connection.lastSuccessAt).getTime() > STALE_AFTER_MS
    ) {
      issues.push({
        code: `STALE_${connection.source}`,
        severity: 'warning',
        title: `${label} : données de plus de 24 h`,
        detail:
          'La synchronisation automatique (toutes les 6 h) ne s’est pas faite. Lancer « Synchroniser maintenant ».',
      })
    }
  }
  if (!overview) return issues

  const ads = overview.ads.current
  const leads = sumSteps(overview.funnel.byChannel).leads
  const unknownLeads = channelSteps(overview.funnel.byChannel, 'UNKNOWN').leads

  if (ads.cost > 0 && leads === 0) {
    issues.push({
      code: 'SPEND_WITHOUT_LEADS',
      severity: 'alert',
      title: 'Dépenses sans aucune demande',
      detail: `${formatMoney(ads.cost)} dépensés sur la période, aucune demande reçue dans RÉCA.`,
    })
  }

  const overCounted = overview.ga4.daily.filter((day) => day.keyEvents > day.sessions && day.keyEvents > 0)
  if (overCounted.length > 0) {
    issues.push({
      code: 'GA4_KEY_EVENTS_OVER_SESSIONS',
      severity: 'warning',
      title: 'GA4 : plus de conversions que de visites',
      detail: `${String(overCounted.length)} jour(s) (${overCounted
        .slice(0, 4)
        .map((day) => formatDay(day.date))
        .join(
          ', ',
        )}${overCounted.length > 4 ? '…' : ''}) : l’événement de conversion est compté plusieurs fois par visite, ou la visite n’est pas mesurée (refus du consentement). Ne pas additionner ce chiffre aux demandes.`,
    })
  }

  if (ads.clicks >= 20 && overview.ga4.current.sessions < ads.clicks * 0.3) {
    issues.push({
      code: 'GA4_UNDER_MEASURED',
      severity: 'warning',
      title: 'GA4 ne voit qu’une partie des visites',
      detail: `${formatInteger(ads.clicks)} clics Google Ads pour ${formatInteger(overview.ga4.current.sessions)} sessions GA4. La mesure ne démarre qu’après consentement : GA4 sous-estime le trafic, les tendances restent lisibles.`,
    })
  }

  if (ads.conversions > 0 && ads.conversions > leads * 1.2) {
    issues.push({
      code: 'ADS_CONVERSIONS_ABOVE_LEADS',
      severity: 'warning',
      title: 'Google Ads compte plus de conversions que RÉCA de demandes',
      detail: `${formatDecimal(ads.conversions)} conversions Google pour ${formatInteger(leads)} demandes reçues. Une conversion Google n’est pas une demande : se fier au chiffre RÉCA.`,
    })
  }

  if (leads > 0 && unknownLeads === leads) {
    issues.push({
      code: 'ATTRIBUTION_MISSING',
      severity: 'info',
      title: 'Provenance des demandes non suivie',
      detail: `Les ${formatInteger(leads)} demandes de la période sont « Inconnue » : le coût par demande et le ROAS Google Ads ne peuvent pas encore être calculés.`,
    })
  } else if (unknownLeads > 0) {
    issues.push({
      code: 'ATTRIBUTION_PARTIAL',
      severity: 'info',
      title: `${formatInteger(unknownLeads)} demande(s) sans provenance`,
      detail: 'Comptées dans le total, jamais attribuées à une source par défaut.',
    })
  }

  // Hausse brutale : un jour au moins deux fois au-dessus de la médiane des 7 jours qui le précèdent.
  const spend = overview.ads.daily
  for (let index = 7; index < spend.length; index += 1) {
    const day = spend[index]
    if (!day) continue
    const base = median(spend.slice(index - 7, index).map((entry) => entry.cost))
    if (base > 0 && day.cost >= base * 2 && day.cost - base >= 10) {
      issues.push({
        code: `SPEND_JUMP_${day.date}`,
        severity: 'info',
        title: `Hausse des dépenses le ${formatDay(day.date)}`,
        detail: `${formatMoney(day.cost)} ce jour-là, contre ${formatMoney(base)} par jour la semaine précédente. Vérifier le budget ou les enchères dans Google Ads.`,
      })
      break
    }
  }

  const lastThree = overview.ga4.daily.slice(-3)
  const recentClicks = spend.slice(-3).reduce((total, day) => total + day.clicks, 0)
  if (lastThree.length === 3 && recentClicks >= 15 && lastThree.every((day) => day.keyEvents === 0)) {
    issues.push({
      code: 'GA4_CONVERSIONS_STOPPED',
      severity: 'warning',
      title: 'Aucune conversion GA4 depuis 3 jours',
      detail: `${formatInteger(recentClicks)} clics Google Ads sur la même période. Vérifier la page « Demande reçue » du site.`,
    })
  }

  const order: Record<HealthSeverity, number> = { alert: 0, warning: 1, info: 2 }
  return issues.sort((a, b) => order[a.severity] - order[b.severity])
}

// ——— Opportunités SEO (Search Console) ———

export type SeoOpportunityKind = 'LOW_CTR' | 'NEAR_FIRST_PAGE' | 'GROWING' | 'DECLINING'

export const seoOpportunityLabels: Record<SeoOpportunityKind, string> = {
  LOW_CTR: 'Beaucoup vue, peu cliquée',
  NEAR_FIRST_PAGE: 'Position 4 à 15',
  GROWING: 'En croissance',
  DECLINING: 'En recul',
}

export interface SeoOpportunity {
  kinds: SeoOpportunityKind[]
  dimension: string
  row: GscBreakdownRow
}

/**
 * Seuils simples, volontairement lisibles; ils ne promettent aucun classement. Une requête = une entrée (ses
 * constats en badges). Les requêtes de marque sont écartées : elles mesurent la notoriété, pas le référencement.
 */
export function seoOpportunities(rows: readonly GscBreakdownRow[]): SeoOpportunity[] {
  const result: SeoOpportunity[] = []
  for (const row of rows) {
    if (isBrandQuery(row.dimension)) continue
    const kinds: SeoOpportunityKind[] = []
    const ctr = ratio(row.clicks, row.impressions) ?? 0
    const previousImpressions = row.previousImpressions ?? 0
    if (row.impressions >= 50 && ctr < 0.02) kinds.push('LOW_CTR')
    else if (row.position !== null && row.position >= 4 && row.position <= 15 && row.impressions >= 10) {
      kinds.push('NEAR_FIRST_PAGE')
    }
    if (row.impressions >= 20 && previousImpressions > 0 && row.impressions >= previousImpressions * 1.5) {
      kinds.push('GROWING')
    } else if (previousImpressions >= 20 && row.impressions <= previousImpressions * 0.5) {
      kinds.push('DECLINING')
    }
    if (kinds.length > 0) result.push({ kinds, dimension: row.dimension, row })
  }
  return result.slice(0, 12)
}

/**
 * Requête de marque (« reca », « groupe reca », et les fautes vues dans Search Console : « re ca », « re:ca »,
 * « reka », « rea » seule). Search Console ne relie jamais une requête à une demande : la distinction sert
 * seulement à lire la visibilité hors notoriété.
 */
export function isBrandQuery(query: string): boolean {
  const value = query.normalize('NFC').trim()
  return /\br[ée][\s:.-]?[ck]a\b/i.test(value) || /^r[ée]a$/i.test(value)
}

// ——— Formats ———

const money = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 2 })
const moneyRound = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
})
const integer = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 0 })
const decimal = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 1 })
const percent = new Intl.NumberFormat('fr-CA', { style: 'percent', maximumFractionDigits: 1 })

export const formatMoney = (value: number) => (value >= 1000 ? moneyRound : money).format(value)
export const formatInteger = (value: number) => integer.format(value)
export const formatDecimal = (value: number) => decimal.format(value)
export const formatOptional = (value: number | null, format: (value: number) => string) =>
  value === null ? '—' : format(value)
export const formatPercent = (value: number) => percent.format(value)
export const formatRoas = (value: number) => `${decimal.format(value)} ×`

/** « +12 % vs période précédente »; `null` sans comparaison possible. */
export function formatChange(value: number | null): string | null {
  if (value === null) return null
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${percent.format(Math.abs(value))} vs période précédente`
}
